/**
 * TS-side view of the design tokens for code that can't read CSS variables
 * (charts, canvas, emails preview). Kept in lockstep with tokens.css —
 * a unit test pins that both files agree on every color.
 *
 * Palette: "Kobalt & Gold" — bright and high-energy.
 */
export const tokens = {
  brand: "#2C55E8",
  brandSoft: "#E0E8FF",
  brandDark: "#1636B0",
  accent: "#F2A007",
  accentSoft: "#FFF1D0",
  accentDark: "#8F5D06",
  canvas: "#F1F4FC",
  surface: "#FFFFFF",
  ink: "#0F1734",
  muted: "#545F7D",
  line: "#DBE1F0",
  success: "#087A40",
  warning: "#AD5200",
  danger: "#D51F3E",
} as const;

export type TokenName = keyof typeof tokens;

/** "124 58 237" (CSS var form) → "#7c3aed". Used by the lockstep test. */
export function rgbTripletToHex(triplet: string): string {
  const parts = triplet.trim().split(/\s+/).map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n) || n < 0 || n > 255)) {
    throw new Error(`invalid rgb triplet: ${triplet}`);
  }
  return (
    "#" + parts.map((n) => n.toString(16).padStart(2, "0")).join("")
  );
}

/** `("#2C55E8", 0.5)` -> `"rgba(44, 85, 232, 0.5)"`.
 *
 * For the handful of places that paint colour in JavaScript rather than in
 * a class — a canvas, an inline overlay — where Tailwind cannot reach and a
 * literal would silently keep the old palette after a rebrand. Exactly how
 * the speaking orb stayed sage green when everything around it turned
 * cobalt. */
export function withAlpha(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  if ([r, g, b].some(Number.isNaN)) throw new Error(`invalid hex: ${hex}`);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
